import { useCallback, useMemo } from 'react';
import { Typography, Slider } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { I18N_KEYS } from '@/language/keys';
import { Input } from '@/UI/Input/Input';
import { Dropdown } from '@/UI/Dropdown/Dropdown';
import { FlexCenVer, FlexCol } from '@/UI/styles';
import { ImportLoraData } from '@/types/node';
import { colorMap } from '../../colors';
import { maxWeight, minWeight } from '../Recipe/Views/LoraView';
import { useLoraView } from '../Recipe/FlowContext';
import { DynamicNode2 } from './DynamicNode/DynamicNode2';
import type { NodeId } from 'web';

const stepWeight = 0.01;

function ImportLoraNode({ id, data }: { id: NodeId; data: ImportLoraData }) {
  const { t } = useTranslation();

  const { selectedIndex, weight, loras = [] } = data;
  const view = useLoraView(id);

  const updateSelectedLora = useCallback(
    (newValue: number) => {
      view.setSelectedIndex(newValue);
    },
    [view],
  );

  const handleWeightChange = useCallback(
    (newValue) => {
      view.setWeight(parseFloat(newValue), true);
    },
    [view],
  );

  const handleWeightChangeCommitted = useCallback(
    (newValue) => {
      view.setWeight(parseFloat(newValue), false);
    },
    [view],
  );

  const options = useMemo(() => {
    return loras.map((l, index) => ({
      id: l.file,
      label: l.name,
      value: index,
    }));
  }, [loras]);

  return (
    <DynamicNode2 id={id} data={data} className="import" handleColor={colorMap.get(data.color)}>
      <FlexCol sx={{ gap: 1 }}>
        <FlexCenVer sx={{ gap: 1 }}>
          <Dropdown
            placement="bottom-start"
            size="large"
            i18nIsDynamicList
            emptyState={
              <Typography variant="body-sm-rg" sx={{ my: 1, display: 'block' }}>
                {t(I18N_KEYS.RECIPE_MAIN.NODES.MULTI_LORA.SELECT_LORA)}
              </Typography>
            }
            matchTriggerWidth
            noOptionsText={t(I18N_KEYS.RECIPE_MAIN.NODES.MULTI_LORA.NO_OPTIONS)}
            sx={{ width: '100%' }}
            options={options}
            value={selectedIndex}
            onChange={(option) => {
              updateSelectedLora(option.value);
            }}
          />
        </FlexCenVer>
        <FlexCenVer>
          <Typography variant="body-sm-rg">{t(I18N_KEYS.GENERAL.LORA_WEIGHT)}</Typography>
          <Slider
            className="nodrag"
            size="small"
            value={weight}
            onChange={(_, newValue) => handleWeightChange(newValue)}
            onChangeCommitted={(_, newValue) => handleWeightChangeCommitted(newValue)}
            aria-labelledby="weight-slider"
            max={maxWeight}
            min={minWeight}
            sx={{ ml: 2 }}
            step={stepWeight}
            valueLabelDisplay="auto"
          />
          <Input
            value={weight}
            size="small"
            onChange={(e) => handleWeightChangeCommitted(e.target.value)}
            inputProps={{
              step: stepWeight,
              min: minWeight,
              max: maxWeight,
              type: 'number',
              'aria-labelledby': 'input-slider',
              style: {
                width: '50px',
                fontSize: '10px',
              },
            }}
            sx={{ ml: 2 }}
          />
        </FlexCenVer>
      </FlexCol>
    </DynamicNode2>
  );
}

export default ImportLoraNode;
